import React, { ReactNode, useState, useMemo } from 'react';
import { debounce } from 'lodash'; // eslint-disable-line
import {
  GENERATIONS,
  filterByGenerations,
  filterByTypesInclusive,
  filterBaseSpecies,
  countBaseSpecies,
  filterGMaxes,
  filterMegas,
  filterRegionals,
  filterBaseFormes,
  filterByStats,
  getBaseStatsAverageGivenSpeciesIds,
  getRandomInt,
  isLegendary,
  isMythical,
  isFullyEvolved,
  isBabyPokemon,
  isUltraBeast,
  isParadoxPokemon,
} from './helper';
import { SpeciesData } from './helper/dex-species';
import _Pokedex from './data/pokedex.json';
import _GenusFlavorText from './data/genus-ftext.json';
import AppContext from './AppContext';

const defaultGensAllowedState = (defaultValue: boolean) => {
  const state: { [key: string]: boolean } = {};
  GENERATIONS.forEach((gen: { name: string, value: number }) => {
    state[gen.value] = defaultValue;
  });
  return state;
};

function AppProvider({ children }: { children: ReactNode }) {
  const Pokedex = _Pokedex as { [speciesId: string]: SpeciesData };
  const GenusFlavorText = _GenusFlavorText as { [key: string]: { ftext: string, genus: string } };

  const [gensAllowed, setGensAllowed] = useState(defaultGensAllowedState(true));
  const [typesSelected, setTypesSelected] = useState<string[]>([]);
  const [megasAllowed, setMegasAllowed] = useState(true);
  const [gmaxAllowed, setGmaxAllowed] = useState(true);
  const [regionalsAllowed, setRegionalsAllowed] = useState(true);
  const [baseSpeciesAllowed, setBaseSpeciesAllowed] = useState(true);
  const [baseFormesAllowed, setBaseFormesAllowed] = useState(true);
  const [hpRange, setHpRange] = useState([1, 255]);
  const [attackRange, setAttackRange] = useState([1, 255]);
  const [defenseRange, setDefenseRange] = useState([1, 255]);
  const [specialAttackRange, setSpecialAttackRange] = useState([1, 255]);
  const [specialDefenseRange, setSpecialDefenseRange] = useState([1, 255]);
  const [speedRange, setSpeedRange] = useState([1, 255]);

  const debouncedSetHpRange = debounce(setHpRange, 275);
  const debouncedSetAttackRange = debounce(setAttackRange, 275);
  const debouncedSetDefenseRange = debounce(setDefenseRange, 275);
  const debouncedSetSpecialAttackRange = debounce(setSpecialAttackRange, 275);
  const debouncedSetSpecialDefenseRange = debounce(setSpecialDefenseRange, 275);
  const debouncedSetSpeedRange = debounce(setSpeedRange, 275);

  const [legendariesOnly, setLegendariesOnly] = useState(false);
  const [mythicalsOnly, setMythicalsOnly] = useState(false);
  const [fullyEvolvedOnly, setFullyEvolvedOnly] = useState(false);
  const [babyPokemonOnly, setBabyPokemonOnly] = useState(false);
  const [ultraBeastsOnly, setUltraBeastsOnly] = useState(false);
  const [paradoxPokemonOnly, setParadoxPokemonOnly] = useState(false);

  const spritePosition: { [key: string]: number[] } = {};
  Object.keys(Pokedex).forEach((speciesId, index) => {
    const topLeftRow = (index % 30) * 40; // eslint-disable-line no-mixed-operators
    const topLeftCol = Math.floor(index / 30) * 40; // eslint-disable-line
    spritePosition[speciesId] = [topLeftRow, topLeftCol];
  });

  const handleSetGensAllowed = (gen: string) => {
    const copy = { ...gensAllowed };
    copy[gen] = !copy[gen];
    setGensAllowed(copy);
  };

  const handleSetAllGenerations = (shouldBeAllChecked: boolean) => {
    setGensAllowed(defaultGensAllowedState(shouldBeAllChecked));
  };

  const handleSetTypesSelected = (type: string) => {
    const checkElementExistIndex = typesSelected.indexOf(type);
    if (checkElementExistIndex === -1) {
      setTypesSelected([...typesSelected, type]);
      return;
    }
    // Type already exist, so remove
    typesSelected.splice(checkElementExistIndex, 1);
    setTypesSelected([...typesSelected]);
  };

  /* Basic filters start */
  let filteredSpeciesIds = Object.keys(Pokedex)
    .filter(filterByGenerations(Pokedex, gensAllowed))
    .filter(filterBaseSpecies(Pokedex, baseSpeciesAllowed))
    .filter(filterBaseFormes(Pokedex, baseFormesAllowed))
    .filter(filterGMaxes(Pokedex, gmaxAllowed))
    .filter(filterMegas(Pokedex, megasAllowed))
    .filter(filterRegionals(Pokedex, regionalsAllowed));

  // Small optimization to make it snappier
  if (typesSelected.length !== 0) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByTypesInclusive(Pokedex, typesSelected));
  }
  if (hpRange[0] !== 1 || hpRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'hp', hpRange));
  }
  if (attackRange[0] !== 1 || attackRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'atk', attackRange));
  }
  if (defenseRange[0] !== 1 || defenseRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'def', defenseRange));
  }
  if (specialAttackRange[0] !== 1 || specialAttackRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'spa', specialAttackRange));
  }
  if (specialDefenseRange[0] !== 1 || specialDefenseRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'spd', specialDefenseRange));
  }
  if (speedRange[0] !== 1 || speedRange[1] !== 255) {
    filteredSpeciesIds = filteredSpeciesIds.filter(filterByStats(Pokedex, 'spe', speedRange));
  }
  /* Basic filters end */

  /* Special filters start */
  const getResultFromSpecialFilter = (speciesId: string): boolean => {
    if (!(
      legendariesOnly
      || mythicalsOnly
      || fullyEvolvedOnly
      || babyPokemonOnly
      || ultraBeastsOnly
      || paradoxPokemonOnly
    )) return true;
    let result = false;
    if (legendariesOnly) result = result || isLegendary(speciesId, Pokedex);
    if (mythicalsOnly) result = result || isMythical(speciesId, Pokedex);
    if (fullyEvolvedOnly) result = result || isFullyEvolved(speciesId, Pokedex);
    if (babyPokemonOnly) result = result || isBabyPokemon(speciesId, Pokedex);
    if (ultraBeastsOnly) result = result || isUltraBeast(speciesId, Pokedex);
    if (paradoxPokemonOnly) result = result || isParadoxPokemon(speciesId, Pokedex);
    return result;
  };
  filteredSpeciesIds = filteredSpeciesIds.filter(getResultFromSpecialFilter);
  /* Special filters end */
  const [clickedSpeciesId, setClickedSpeciesId] = useState(
    filteredSpeciesIds[getRandomInt(filteredSpeciesIds.length)],
  );

  const totalBaseSpecies = countBaseSpecies(Pokedex, filteredSpeciesIds);
  const totalFilteredSpecies = filteredSpeciesIds.length;
  const averageStatsAllSpecies = getBaseStatsAverageGivenSpeciesIds(Pokedex, filteredSpeciesIds);

  const value = useMemo(() => ({
    dex: Pokedex,
    filteredSpeciesIds,
    megasAllowed,
    setMegasAllowed,
    gmaxAllowed,
    setGmaxAllowed,
    regionalsAllowed,
    setRegionalsAllowed,
    baseSpeciesAllowed,
    setBaseSpeciesAllowed,
    clickedSpeciesId,
    setClickedSpeciesId,
    gensAllowed,
    handleSetGensAllowed,
    handleSetAllGenerations,
    baseFormesAllowed,
    setBaseFormesAllowed,
    typesSelected,
    handleSetTypesSelected,
    totalBaseSpecies,
    totalFilteredSpecies,
    averageStatsAllSpecies,
    spritePosition,
    hpRange,
    debouncedSetHpRange,
    attackRange,
    debouncedSetAttackRange,
    defenseRange,
    debouncedSetDefenseRange,
    specialAttackRange,
    debouncedSetSpecialAttackRange,
    specialDefenseRange,
    debouncedSetSpecialDefenseRange,
    speedRange,
    debouncedSetSpeedRange,
    GenusFlavorText,
    legendariesOnly,
    setLegendariesOnly,
    mythicalsOnly,
    setMythicalsOnly,
    fullyEvolvedOnly,
    setFullyEvolvedOnly,
    babyPokemonOnly,
    setBabyPokemonOnly,
    paradoxPokemonOnly,
    setParadoxPokemonOnly,
    ultraBeastsOnly,
    setUltraBeastsOnly,
  }), [
    gensAllowed,
    megasAllowed,
    gmaxAllowed,
    baseSpeciesAllowed,
    clickedSpeciesId,
    regionalsAllowed,
    baseFormesAllowed,
    typesSelected,
    hpRange,
    attackRange,
    defenseRange,
    specialAttackRange,
    specialDefenseRange,
    speedRange,
    legendariesOnly,
    mythicalsOnly,
    fullyEvolvedOnly,
    babyPokemonOnly,
    paradoxPokemonOnly,
    ultraBeastsOnly,
  ]);

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}

export default AppProvider;
